/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "../_shared/withRouter";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LearnerAuthShell from "./learnerAuthShell";
import InputComponent from "../template/_common/inputComponent";
import LanguageSelector from "../template/_common/languageSelector";
import { globalAlert } from "../actions/commonActions";
import { SUPPORTED_LANGUAGES } from "../i18n/languages";
import { emailShapeOk, resolveLmsShortName, learnerPath } from "./_learnerConfig";
import {
  learnerSendOtpApi,
  learnerVerifyOtpApi,
  learnerRegionsApi,
  learnerRegisterApi,
} from "./_learnerActions";

// ─────────────────────────────────────────────────────────────────────────────
// ESHS LMS — 3-step (email-first) registration wizard.
// Holds { email, orgName, domainApproved, verified } across steps and drives:
//   1. email → send OTP   2. verify OTP   3. registration form   4. confirmation
// Every server envelope is unwrapped as { status, data, message }; app-status
// 204 is a SOFT validation error rendered inline (never thrown / never a fatal
// toast). The OTP is never echoed into the URL and never logged.
// ─────────────────────────────────────────────────────────────────────────────

// Small countdown helper (seconds → live remaining, with start/stop).
const useCountdown = () => {
  const [remaining, setRemaining] = useState(0);
  const ref = useRef(null);
  const stop = () => {
    if (ref.current) clearInterval(ref.current);
    ref.current = null;
  };
  const start = (seconds) => {
    stop();
    setRemaining(Math.max(0, Math.floor(seconds || 0)));
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          stop();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };
  useEffect(() => stop, []);
  return { remaining, start, stop };
};

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return `${m}:${ss}`;
};

// Marketing newsletter sign-up pages, one per service. Keyed off the region's
// `code` from /public/config/regions ("ESSEX" / "SUFFOLK"), falling back to a
// case-insensitive name match so a region seeded without a code still resolves.
// Regions outside these two services get no link (the block just stays hidden).
const NEWSLETTER_SIGNUPS = [
  {
    match: "suffolk",
    service: "SSHS",
    url: "https://suffolksexualhealthservice.org.uk/metform-form/newsletter-sign-up/",
  },
  {
    match: "essex",
    service: "ESHS",
    url: "https://essexsexualhealthservice.org.uk/metform-form/eshs-newsletter-sign-up/",
  },
];

const newsletterFor = (region) => {
  if (!region) return null;
  const code = String(region.code || "").toLowerCase();
  const name = String(region.name || "").toLowerCase();
  return (
    NEWSLETTER_SIGNUPS.find((n) => code === n.match || name.includes(n.match)) || null
  );
};

// Pull "N" out of a "please wait N seconds" style soft-error message; default 60.
const parseCooldown = (msg = "") => {
  const m = /(\d+)\s*second/i.exec(msg);
  return m ? Number(m[1]) : 60;
};

const shortNameSetupError = (t) =>
  t(
    "learner.lms.errors.shortNameSetup",
    "This learning portal is not configured with an organisation short name. Please contact your administrator."
  );

const registrationSoftError = (message, t) =>
  /organisation short name is mandatory/i.test(message || "")
    ? shortNameSetupError(t)
    : message || t("learner.lms.errors.generic", "Please try again.");

const mergeOrganisation = (current, incoming) => ({
  ...(current || {}),
  ...(incoming || {}),
});

const RegistrationWizard = (props) => {
  const { t } = useTranslation();
  const lmsShortName = useMemo(() => resolveLmsShortName(), []);
  const portalOrganisation = props?.portalInfo?.organisation || null;
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    email: "",
    orgName: portalOrganisation?.name || "",
    organisation: portalOrganisation,
    domainApproved: false,
    verified: false,
    expiresInSeconds: 0,
    activationEmailSent: null,
    registrationMessage: "",
  });
  const patch = (p) => setData((d) => ({ ...d, ...p }));

  const steps = [
    t("learner.lms.register.stepEmail", "Email"),
    t("learner.lms.register.stepVerify", "Verify"),
    t("learner.lms.register.stepDetails", "Details"),
  ];

  return (
    <LearnerAuthShell
      title={t("learner.lms.register.title", "Create your account")}
      heading={
        step === 4
          ? undefined
          : t("learner.lms.register.title", "Create your account")
      }
      headingExtra={
        step < 4 ? (
          // <LanguageSelector
          //   variant="dark"
          //   languages={SUPPORTED_LANGUAGES}
          //   value={localStorage.getItem("userLanguage") || "en"}
          //   label={t("common.selectLanguage", "Select Language")}
          // />
          null
        ) : null
      }
      subheading={
        data.orgName
          ? t("learner.lms.register.withOrg", "Registering with {{name}}", {
            name: data.orgName,
          })
          : undefined
      }
      footer={
        step < 4 ? (
          <span style={{ fontSize: '16px' }}>
            {t("learner.lms.register.haveAccount", "Already have an account?")}{" "}
            <Link to={learnerPath(lmsShortName, "login")} className="lms-auth-link text-decoration-none" style={{ color: '#d7eff5', fontSize: '16px' }}>
              {t("learner.lms.login.title", "Log in")}
            </Link>
          </span>
        ) : null
      }
      organisation={data.organisation}
    >
      {step < 4 && (
        <ol className="lms-auth-steps py-4" aria-label="progress">
          {steps.map((label, i) => {
            const n = i + 1;
            const cls =
              n < step ? "done" : n === step ? "current" : "upcoming";
            return (
              <li key={label} className={`lms-auth-step ${cls}`}>
                <span className="lms-auth-step-dot" style={{ fontSize: '14px', width: '28px', height: '28px', lineHeight: '28px' }}>
                  {n < step ? <i className="fas fa-check"></i> : n}
                </span>
                <span className="lms-auth-step-label" style={{ fontSize: '16px' }}>{label}</span>
              </li>
            );
          })}
        </ol>
      )}

      {step === 1 && (
        <EmailStep
          t={t}
          lmsShortName={lmsShortName}
          initialEmail={data.email}
          onDone={({ email, organisation, domainApproved, expiresInSeconds }) => {
            const nextOrganisation = mergeOrganisation(data.organisation, organisation);
            patch({
              email,
              organisation: nextOrganisation,
              orgName: nextOrganisation?.name || "",
              domainApproved,
              expiresInSeconds,
            });
            setStep(2);
          }}
        />
      )}

      {step === 2 && (
        <OtpStep
          t={t}
          lmsShortName={lmsShortName}
          email={data.email}
          expiresInSeconds={data.expiresInSeconds}
          onBack={() => setStep(1)}
          onOrganisation={(organisation) =>
            patch({
              organisation: mergeOrganisation(data.organisation, organisation),
              orgName: organisation?.name || data.orgName,
            })
          }
          onVerified={({ domainApproved, organisation }) => {
            const nextOrganisation = mergeOrganisation(data.organisation, organisation);
            patch({
              verified: true,
              ...(domainApproved != null ? { domainApproved } : {}),
              ...(organisation
                ? { organisation: nextOrganisation, orgName: nextOrganisation?.name || data.orgName }
                : {}),
            });
            setStep(3);
          }}
        />
      )}

      {step === 3 && (
        <DetailsStep
          t={t}
          lmsShortName={lmsShortName}
          email={data.email}
          onExpired={() => {
            patch({ verified: false });
            setStep(1);
          }}
          onRegistered={({ activationEmailSent, message }) => {
            patch({ activationEmailSent, registrationMessage: message || "" });
            setStep(4);
          }}
        />
      )}

      {step === 4 && (
        <ConfirmStep
          t={t}
          domainApproved={data.domainApproved}
          activationEmailSent={data.activationEmailSent}
          registrationMessage={data.registrationMessage}
        />
      )}
    </LearnerAuthShell>
  );
};

/* ───────────── Step 1: email → send OTP ───────────── */

const EmailStep = ({ t, lmsShortName, initialEmail, onDone }) => {
  const [email, setEmail] = useState(initialEmail || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!emailShapeOk(email)) {
      setError(t("learner.lms.errors.emailInvalid", "Enter a valid email address."));
      return;
    }
    if (!lmsShortName) {
      setError(shortNameSetupError(t));
      return;
    }
    setBusy(true);
    try {
      const res = await learnerSendOtpApi({
        lmsShortName,
        email: email.trim(),
      });
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        onDone({
          email: email.trim(),
          organisation: data?.organisation || null,
          domainApproved: !!data?.domainApproved,
          expiresInSeconds: data?.expiresInSeconds,
        });
      } else if (status === 204) {
        setError(registrationSoftError(message, t));
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      globalAlert("error", err?.response?.data?.message);
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="form-own lms-auth-form" noValidate>
      <div className="form-group reg-date login-page">
        <InputComponent
          placeholder={t("user.login.emailPlaceholder", "Email")}
          label={t("user.login.emailPlaceholder", "Email")}
          name="email"
          type="text"
          keyType="email"
          theme="primary"
          color="#d7eff5"
          maxLength={40}
          value={email}
          autoComplete="email"
          handleCallback={(val) => {
            setEmail(val);
            if (error) setError("");
          }}
        />
        {error && (
          <span id="reg-email-err" className="error-text font-12 pl-2" role="alert">
            {error}
          </span>
        )}
      </div>
      <button
        type="submit"
        className="btn primary-btn btn-block lms-auth-submit w-100"
        disabled={busy}
        aria-label={
          busy
            ? t("learner.lms.register.sendingCode", "Sending code")
            : t("learner.lms.register.sendCode", "Send code")
        }
      >
        {busy ? (
          <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
        ) : (
          t("learner.lms.register.sendCode", "Send code")
        )}
      </button>
    </form>
  );
};

/* ───────────── Step 2: verify OTP ───────────── */

const OTP_LEN = 6;

const OtpStep = ({
  t,
  lmsShortName,
  email,
  expiresInSeconds,
  onBack,
  onOrganisation,
  onVerified,
}) => {
  const [digits, setDigits] = useState(Array(OTP_LEN).fill(""));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const boxes = useRef([]);
  const ttl = useCountdown();
  const cooldown = useCountdown();

  // The parent triggered send-otp; open the TTL window from the server's
  // expiresInSeconds (falling back to the documented 10-minute default).
  useEffect(() => {
    ttl.start(expiresInSeconds || 10 * 60);
    cooldown.start(60);
    boxes.current[0]?.focus();
  }, []);

  const code = digits.join("");

  const setDigit = (i, v) => {
    const c = v.replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const n = [...d];
      n[i] = c;
      return n;
    });
    if (error) setError("");
    if (c && i < OTP_LEN - 1) boxes.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      boxes.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      boxes.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < OTP_LEN - 1) {
      boxes.current[i + 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LEN);
    if (!text) return;
    e.preventDefault();
    const n = Array(OTP_LEN).fill("");
    text.split("").forEach((c, i) => (n[i] = c));
    setDigits(n);
    boxes.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
  };

  const resend = async () => {
    if (cooldown.remaining > 0) return;
    setError("");
    if (!lmsShortName) {
      setError(shortNameSetupError(t));
      return;
    }
    try {
      const res = await learnerSendOtpApi({ lmsShortName, email });
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        if (data?.organisation) onOrganisation(data.organisation);
        setDigits(Array(OTP_LEN).fill(""));
        ttl.start(data?.expiresInSeconds || 10 * 60);
        cooldown.start(60);
        boxes.current[0]?.focus();
        globalAlert("success", t("learner.lms.register.codeResent", "A new code has been sent."));
      } else if (status === 204) {
        cooldown.start(parseCooldown(message));
        setError(registrationSoftError(message, t));
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      globalAlert("error", err?.response?.data?.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== OTP_LEN) {
      setError(t("learner.lms.errors.otpLength", "Enter the 6-digit code."));
      return;
    }
    if (!lmsShortName) {
      setError(shortNameSetupError(t));
      return;
    }
    setBusy(true);
    try {
      const res = await learnerVerifyOtpApi({ lmsShortName, email, otp: code });
      const { status, data, message } = res?.data || {};
      if (status === 200 && data?.verified) {
        // verify-otp echoes domainApproved — use it (authoritative at this point)
        // to drive the confirmation screen's approved/pending branch.
        onVerified({
          domainApproved: data?.domainApproved,
          organisation: data?.organisation || null,
        });
      } else if (status === 204) {
        setError(
          /organisation short name is mandatory/i.test(message || "")
            ? shortNameSetupError(t)
            : message || t("learner.lms.errors.otpIncorrect", "Incorrect code.")
        );
        setDigits(Array(OTP_LEN).fill(""));
        boxes.current[0]?.focus();
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      globalAlert("error", err?.response?.data?.message);
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="form-own lms-auth-form" noValidate>
      <p className="lms-auth-hint">
        {t("learner.lms.register.otpSentTo", "We sent a 6-digit code to")}{" "}
        <strong>{email}</strong>
      </p>

      <div
        className="lms-otp-row"
        role="group"
        aria-label={t("learner.lms.fields.otp", "Verification code")}
        aria-describedby={error ? "otp-err" : undefined}
        onPaste={onPaste}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (boxes.current[i] = el)}
            className={`form-control lms-auth-input ${error ? "is-invalid" : ""}`}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={d}
            aria-label={t("learner.lms.fields.otpDigit", "Digit {{n}}", { n: i + 1 })}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            style={{ borderRadius: '8px' }}
          />
        ))}
      </div>

      {error && (
        <div id="otp-err" className="error-text font-12 text-center" role="alert">
          {error}
        </div>
      )}

      <div className="lms-auth-otp-meta">
        <span className={ttl.remaining > 0 ? "" : "text-danger"}>
          {ttl.remaining > 0
            ? t("learner.lms.register.expiresIn", "Code expires in {{time}}", {
              time: fmt(ttl.remaining),
            })
            : t("learner.lms.register.codeExpired", "Code expired — request a new one.")}
        </span>
        {cooldown.remaining > 0 ? (
          <span className="text-muted">
            {t("learner.lms.register.resendIn", "Resend in {{n}}s", {
              n: cooldown.remaining,
            })}
          </span>
        ) : (
          <button type="button" className="lms-auth-linkbtn" onClick={resend}>
            {t("learner.lms.register.resendCode", "Resend code")}
          </button>
        )}
      </div>

      <button
        type="submit"
        className="btn primary-btn btn-block lms-auth-submit w-100"
        disabled={busy || code.length !== OTP_LEN}
        aria-label={
          busy
            ? t("learner.lms.register.verifying", "Verifying")
            : t("learner.lms.register.verify", "Verify")
        }
      >
        {busy ? (
          <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
        ) : (
          t("learner.lms.register.verify", "Verify")
        )}
      </button>
      <button type="button" className="lms-auth-linkbtn d-block mx-auto mt-2" onClick={onBack}>
        {t("learner.lms.register.changeEmail", "Use a different email")}
      </button>
    </form>
  );
};

/* ───────────── Step 3: registration form ───────────── */

const DetailsStep = ({ t, lmsShortName, email, onExpired, onRegistered }) => {
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    confirmEmail: "",
    region: "",
    district: "",
    learnerOrganisation: "",
    jobTitle: "",
    essexConfirm: false,
    newsletterOptIn: false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const firstErrRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await learnerRegionsApi({ lmsShortName });
        if (res?.data?.status === 200) {
          const d = res?.data?.data;
          setRegions(Array.isArray(d) ? d : d?.regions || []);
        } else globalAlert("error", res?.data?.message);
      } catch (err) {
        globalAlert("error", err?.response?.data?.message);
      }
    })();
  }, []);

  const selectedRegion = useMemo(
    () => regions.find((x) => x._id === form.region),
    [regions, form.region]
  );
  const districts = useMemo(() => selectedRegion?.districts || [], [selectedRegion]);
  const regionConfirmName =
    selectedRegion?.name || t("learner.lms.fields.selectedRegion", "the selected region");
  const newsletter = useMemo(() => newsletterFor(selectedRegion), [selectedRegion]);

  const set = (k, v) =>
    setForm((f) => {
      // Changing region resets the dependent district.
      if (k === "region") return { ...f, region: v, district: "", essexConfirm: false };
      return { ...f, [k]: v };
    });

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = t("learner.lms.errors.firstNameRequired", "First name is required.");
    if (!form.lastName.trim()) e.lastName = t("learner.lms.errors.lastNameRequired", "Last name is required.");
    if (!form.learnerOrganisation || !form.learnerOrganisation.trim()) e.learnerOrganisation = t("learner.lms.errors.organisationRequired", "Organisation is required.");
    if (!form.region) e.region = t("learner.lms.errors.regionRequired", "Region is required.");
    if (!form.district) e.district = t("learner.lms.errors.districtRequired", "District is required.");
    if (!form.essexConfirm) e.essexConfirm = t("learner.lms.errors.essexConfirmRequired", "Confirmation is required.");
    return e;
  };

  const focusFirst = (e) => {
    const order = ["firstName", "lastName", "confirmEmail", "learnerOrganisation", "region", "district", "essexConfirm"];
    const first = order.find((k) => e[k]);
    if (first) document.getElementById(`reg-${first}`)?.focus();
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      focusFirst(e);
      return;
    }
    if (!lmsShortName) {
      setErrors((previous) => ({
        ...previous,
        form: shortNameSetupError(t),
      }));
      return;
    }
    setBusy(true);
    try {
      const res = await learnerRegisterApi({
        lmsShortName,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email,
        confirmEmail: email,
        region: form.region,
        district: form.district,
        learnerOrganisation: form.learnerOrganisation.trim(),
        jobTitle: form.jobTitle.trim(),
        newsletterOptIn: form.newsletterOptIn,
      });
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        onRegistered({
          activationEmailSent: data?.activationEmailSent,
          message,
        });
      } else if (status === 204) {
        // Soft errors: verification expired sends the user back to step 1;
        // field-level messages render inline.
        if (/organisation short name is mandatory/i.test(message || "")) {
          setErrors((prev) => ({ ...prev, form: shortNameSetupError(t) }));
        } else if (/verify/i.test(message || "")) {
          globalAlert("warning", message);
          onExpired();
        } else if (/match/i.test(message || "")) {
          setErrors((prev) => ({ ...prev, confirmEmail: email }));
        } else if (/region|district/i.test(message || "")) {
          setErrors((prev) => ({ ...prev, region: message }));
        } else {
          setErrors((prev) => ({ ...prev, form: message }));
        }
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      globalAlert("error", err?.response?.data?.message);
    }
    setBusy(false);
  };

  const field = (id, label, node, err) => (
    <div className="form-group">
      <label htmlFor={`reg-${id}`} className="lms-auth-label">
        {label}
      </label>
      {node}
      {err && (
        <span id={`reg-${id}-err`} className="error-text font-12 pl-2" role="alert">
          {err}
        </span>
      )}
    </div>
  );

  const inputField = (id, label, value, onChange, err, options = {}) => (
    <div className="form-group reg-date login-page">
      <InputComponent
        placeholder={label}
        label={label}
        name={id}
        type={options.type || "text"}
        theme="primary"
        color="#d7eff5"
        maxLength={options.maxLength || 60}
        disabled={options.disabled}
        value={value}
        autoComplete={options.autoComplete || "off"}
        handleCallback={(val) => {
          onChange(val);
          if (err) setErrors((previous) => ({ ...previous, [id]: "" }));
        }}
      />
      {err && (
        <span id={`reg-${id}-err`} className="error-text font-12 pl-2" role="alert">
          {err}
        </span>
      )}
    </div>
  );

  return (
    <form onSubmit={submit} className="form-own lms-auth-form" noValidate ref={firstErrRef}>
      <div className="lms-auth-grid2">
        {inputField(
          "firstName",
          t("learner.lms.fields.firstName", "First name") + " *",
          form.firstName,
          (val) => set("firstName", val),
          errors.firstName,
          { autoComplete: "given-name", maxLength: 40 }
        )}
        {inputField(
          "lastName",
          t("learner.lms.fields.lastName", "Last name") + " *",
          form.lastName,
          (val) => set("lastName", val),
          errors.lastName,
          { autoComplete: "family-name", maxLength: 40 }
        )}
      </div>

      {inputField(
        "email",
        t("learner.lms.fields.email", "Email address") + " *",
        email,
        () => { },
        null,
        { disabled: true, autoComplete: "email", maxLength: 40 }
      )}
      {/* {field(
        "confirmEmail",
        t("learner.lms.fields.confirmEmail", "Confirm email") + " *",
        <input
          id="reg-confirmEmail"
          type="email"
          className={`form-control lms-auth-input ${errors.confirmEmail ? "is-invalid" : ""}`}
          value={form.confirmEmail}
          autoComplete="off"
          aria-invalid={!!errors.confirmEmail}
          aria-describedby={errors.confirmEmail ? "reg-confirmEmail-err" : undefined}
          onChange={(e) => set("confirmEmail", e.target.value)}
        />,
        errors.confirmEmail
      )} */}

      <div className="lms-auth-grid2 " style={{ marginBottom: '13px' }}>
        {field(
          "region",
          t("learner.lms.fields.region", "Region") + " *",
          <select
            id="reg-region"
            className={`form-control lms-auth-input ${errors.region ? "is-invalid" : ""}`}
            value={form.region}
            aria-invalid={!!errors.region}
            aria-describedby={errors.region ? "reg-region-err" : undefined}
            onChange={(e) => {
              set("region", e.target.value);
              if (errors.region) setErrors((p) => ({ ...p, region: "" }));
            }}
          >
            <option value="">{t("learner.lms.fields.selectRegion", "Select region")}</option>
            {regions.map((r) => (
              <option key={r._id} value={r._id}>
                {r.name}
              </option>
            ))}
          </select>,
          errors.region
        )}
        {field(
          "district",
          t("learner.lms.fields.district", "District") + " *",
          <select
            id="reg-district"
            className={`form-control lms-auth-input ${errors.district ? "is-invalid" : ""}`}
            value={form.district}
            disabled={!form.region}
            aria-invalid={!!errors.district}
            aria-describedby={errors.district ? "reg-district-err" : undefined}
            onChange={(e) => {
              set("district", e.target.value);
              if (errors.district) setErrors((p) => ({ ...p, district: "" }));
            }}
          >
            <option value="">
              {form.region
                ? t("learner.lms.fields.selectDistrict", "Select district")
                : t("learner.lms.fields.selectRegionFirst", "Select a region first")}
            </option>
            {districts.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </select>,
          errors.district
        )}
      </div>

      {inputField(
        "learnerOrganisation",
        t("learner.lms.fields.learnerOrganisation", "Your organisation / school") + "*",
        form.learnerOrganisation,
        (val) => set("learnerOrganisation", val),
        errors.learnerOrganisation,
        { autoComplete: "organization", maxLength: 80 }
      )}

      {inputField(
        "jobTitle",
        t("learner.lms.fields.jobTitle", "Job title"),
        form.jobTitle,
        (val) => set("jobTitle", val),
        null,
        { autoComplete: "organization-title", maxLength: 80 }
      )}

      <div className="form-group">
        <div className="form-check">
          <input
            id="reg-essexConfirm"
            type="checkbox"
            className={`form-check-input ${errors.essexConfirm ? "is-invalid" : ""}`}
            checked={form.essexConfirm}
            aria-invalid={!!errors.essexConfirm}
            aria-describedby={errors.essexConfirm ? "reg-essexConfirm-err" : undefined}
            onChange={(e) => {
              set("essexConfirm", e.target.checked);
              if (errors.essexConfirm) setErrors((p) => ({ ...p, essexConfirm: "" }));
            }}
          />
          <label htmlFor="reg-essexConfirm" className="form-check-label lms-auth-label" style={{ color: '#fff' }}>
            {t(
              "learner.lms.register.regionConfirm",
              "I confirm I am based in {{region}}:  region and work with young people in an educational setting. *",
              { region: regionConfirmName }
            )}
          </label>
        </div>
        {errors.essexConfirm && (
          <span id="reg-essexConfirm-err" className="error-text font-12 pl-2" role="alert">
            {errors.essexConfirm}
          </span>
        )}
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            id="reg-newsletterOptIn"
            type="checkbox"
            className="form-check-input"
            checked={form.newsletterOptIn}
            onChange={(e) => set("newsletterOptIn", e.target.checked)}
          />
          <label htmlFor="reg-newsletterOptIn" className="form-check-label lms-auth-label">
            {t("learner.lms.fields.newsletterOptInQuarterly", "I would like to receive the {{service}} quarterly newsletter by email.", {
              service: newsletter?.service || "ESHS",
            })}
          </label>
          {/* Inside .form-check so the link lines up under the label text, not
              under the checkbox itself. */}
          {newsletter && (
            <a
              className="lms-auth-link d-inline-block mt-1"
              href={newsletter.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("learner.lms.fields.newsletterSignupLink", "{{service}} newsletter sign-up page", {
                service: newsletter.service,
              })}
              <span className="sr-only">
                {t("learner.lms.opensInNewTab", " (opens in a new tab)")}
              </span>
              <i className="fas fa-external-link-alt ml-1 font-12" aria-hidden="true"></i>
            </a>
          )}
        </div>
      </div>

      {errors.form && (
        <div className="error-text font-12 text-center mb-2" role="alert">
          {errors.form}
        </div>
      )}

      <button
        type="submit"
        className="btn primary-btn btn-block lms-auth-submit w-100"
        disabled={busy}
        aria-label={
          busy
            ? t("learner.lms.register.creatingAccount", "Creating account")
            : t("learner.lms.register.submit", "Create account")
        }
      >
        {busy ? (
          <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
        ) : (
          t("learner.lms.register.submit", "Create account")
        )}
      </button>
    </form>
  );
};

/* ───────────── Step 4: confirmation (branches on approval) ───────────── */

const ConfirmStep = ({ t, domainApproved, activationEmailSent, registrationMessage }) => (
  <div className="lms-auth-confirm text-center">
    <div className={`lms-auth-confirm-icon ${domainApproved && activationEmailSent !== false ? "ok" : "pending"}`}>
      <i
        className={`fas ${domainApproved && activationEmailSent !== false
          ? "fa-envelope-open-text"
          : "fa-hourglass-half"
          }`}
      ></i>
    </div>
    {domainApproved && activationEmailSent === false ? (
      <>
        <h2 className="lms-auth-title">
          {t("learner.lms.confirm.activationIssueTitle", "Account approved")}
        </h2>
        <p className="lms-auth-sub">
          {registrationMessage ||
            t(
              "learner.lms.confirm.activationIssueBody",
              "Your account was approved, but we could not send the activation email. Please contact support."
            )}
        </p>
      </>
    ) : domainApproved ? (
      <>
        <h2 className="lms-auth-title">
          {t("learner.lms.confirm.checkEmailTitle", "Check your email")}
        </h2>
        <p className="lms-auth-sub my-3">
          {t(
            "learner.lms.confirm.checkEmailBody",
            "We've sent you a link to activate your account. Set your password to finish, then log in."
          )}
        </p>
      </>
    ) : (
      <>
        <h2 className="lms-auth-title">
          {t("learner.lms.confirm.pendingTitle", "Awaiting approval")}
        </h2>
        <p className="lms-auth-sub">
          {t(
            "learner.lms.confirm.pendingBody",
            "You'll receive an email within the next 72 hours once it's been approved. You won't be able to log in until then."
          )}
        </p>
      </>
    )}
    <Link to={learnerPath(resolveLmsShortName(), "login")} className="btn primary-btn btn-block lms-auth-submit mt-3 py-2 w-100">
      {t("learner.lms.confirm.backToLogin", "Back to login")}
    </Link>
  </div>
);

export default withRouter(RegistrationWizard);
